import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React from 'react';
import { Form } from 'react-bootstrap';
import Select from 'react-select';
import { useTrappingZoneTrapLures } from '../hooks/useTrappingZoneTrapLures';
import { useStores } from '../stores/useStores';

function TrapForm({
  handleSubmit,
  handleChange,
  handleBlur,
  values,
  errors,
  setFieldValue,
  trapCoords,
  setTrapCoords,
  chargedInput = true,
  map
}) {
  const { traplogStore } = useStores();

  const trappingZone = React.useMemo(
    () =>
      traplogStore.trappingZones.find(
        (zone) => zone.id === values.trappingZone.value
      ),
    [traplogStore.trappingZones, values.trappingZone.value]
  );

  const trapLures = useTrappingZoneTrapLures(trappingZone);

  return (
    <Form noValidate onSubmit={handleSubmit}>
      {/* Trapping Zone */}
      <Form.Group controlId='trapping_zone_id'>
        <Form.Label>Production Site</Form.Label>
        <Select
          options={traplogStore.trappingZones.map((trappingZone) => {
            return {
              value: trappingZone.id,
              label: trappingZone.name
            };
          })}
          defaultValue={values.trappingZone}
          onChange={(option) => {
            setFieldValue('trappingZone', option);
            if (!map) return;
            if (trapCoords.currentZone !== option.label) {
              setTrapCoords({
                ...trapCoords,
                valid: false,
                currentZone: option.label
              });
            } else {
              setTrapCoords({
                ...trapCoords,
                valid: true,
                currentZone: option.label
              });
            }
          }}
        />
        <Form.Control.Feedback
          type='invalid'
          style={{ display: errors.trappingZone ? 'inline-block' : 'none' }}
        >
          {errors.trappingZone}
        </Form.Control.Feedback>
      </Form.Group>

      {/* Code */}
      <Form.Group controlId='code'>
        <Form.Label>Trap Id</Form.Label>
        <Form.Control
          type='text'
          onChange={handleChange}
          onBlur={handleBlur}
          value={values.code}
          placeholder='Trap Id'
          isInvalid={errors.code}
        />
        <Form.Control.Feedback type='invalid'>
          {errors.code}
        </Form.Control.Feedback>
      </Form.Group>

      {/* Name */}
      <Form.Group controlId='name'>
        <Form.Label>Name</Form.Label>
        <Form.Control
          type='text'
          onChange={handleChange}
          onBlur={handleBlur}
          value={values.name}
          placeholder='Trap Name'
          isInvalid={errors.name}
        />
        <Form.Control.Feedback type='invalid'>
          {errors.name}
        </Form.Control.Feedback>
      </Form.Group>

      {/* Location Description */}
      <Form.Group controlId='location_description'>
        <Form.Label>Location Description</Form.Label>
        <Form.Control
          as='textarea'
          onChange={handleChange}
          onBlur={handleBlur}
          value={values.location_description}
          placeholder='Location'
          isInvalid={errors.location_description}
        />
        <Form.Control.Feedback type='invalid'>
          {errors.location_description}
        </Form.Control.Feedback>
      </Form.Group>

      {/* Trap Lure */}
      <Form.Group controlId='trap_lure_id'>
        <Form.Label>Trap Lure</Form.Label>
        <Select
          options={trapLures.map((trapLure) => {
            return {
              value: trapLure.id,
              label: `${trapLure.name} x ${trapLure.concentration}`
            };
          })}
          isLoading={traplogStore.loadingTrapLures}
          defaultValue={values.trapLure}
          onChange={(option) => setFieldValue('trapLure', option)}
        />
        {errors.trapLure ? (
          <div className='text-danger'>{errors.trapLure}</div>
        ) : null}
      </Form.Group>

      {/* Comments */}
      <Form.Group controlId='comments'>
        <Form.Label>Comments</Form.Label>
        <Form.Control
          as='textarea'
          onChange={handleChange}
          onBlur={handleBlur}
          value={values.comments}
          placeholder='Comments'
          isInvalid={errors.comments}
        />
        <Form.Control.Feedback type='invalid'>
          {errors.comments}
        </Form.Control.Feedback>
      </Form.Group>

      {/* Setup */}
      <Form.Group controlId='setup_at'>
        <Form.Label>Setup</Form.Label>
        <Form.Control
          type='date'
          onChange={handleChange}
          onBlur={handleBlur}
          isInvalid={errors.setup_at}
          defaultValue={values.setup_at}
        />
        <Form.Control.Feedback type='invalid'>
          {errors.setup_at}
        </Form.Control.Feedback>
      </Form.Group>

      {/* Charged */}
      {chargedInput && (
        <Form.Group controlId='charged_at'>
          <Form.Label>Charged</Form.Label>
          <Form.Control
            type='date'
            onChange={handleChange}
            onBlur={handleBlur}
            isInvalid={errors.charged_at}
            defaultValue={values.charged_at}
          />
          <Form.Control.Feedback type='invalid'>
            {errors.charged_at}
          </Form.Control.Feedback>
        </Form.Group>
      )}
    </Form>
  );
}

TrapForm.propTypes = {
  fProps: PropTypes.any,
  trapCoords: PropTypes.object,
  setTrapCoords: PropTypes.func
};

export default observer(TrapForm);
